import { appHttpService } from '@/services/standard/http-service';
import { models } from 'cesg-shared';

class ProjectService {
  async getAll(): Promise<models.ProjectResponse[]> {
    const projects = await appHttpService.request
      .url('/projects')
      .get()
      .json<models.ProjectResponse[]>();
    return projects;
  }

  async getProjectDetails(
    projectId: string,
  ): Promise<models.ProjectDetailsResponse> {
    const project = await appHttpService.request
      .url('/projects/details/' + projectId)
      .get()
      .json<models.ProjectDetailsResponse>();
    return project;
  }

  async getProjectnames(): Promise<models.ListProjectNamesResponse> {
    const projects = await appHttpService.request
      .url('/projects/names')
      .get()
      .json<models.ListProjectNamesResponse>();
    return projects;
  }

  async getProjectname(id: string): Promise<models.ProjectNameResponse> {
    const name = await appHttpService.request
      .url(`/projects/names/${id}`)
      .get()
      .json<models.ProjectNameResponse>();
    return name;
  }

  async get(id: string): Promise<models.GetProjectResponse> {
    const project = await appHttpService.request
      .url(`/projects/${id}`)
      .get()
      .json<models.GetProjectResponse>();
    return project;
  }

  async put(
    id: string,
    body: models.UpdateProjectBody,
  ): Promise<models.GetProjectResponse> {
    const project = await appHttpService.request
      .url(`/projects/${id}`)
      .put(body)
      .json<models.GetProjectResponse>();
    return project;
  }

  async updateProjectNotes(
    id: string,
    notes: string,
  ): Promise<models.GetProjectNotesResponse> {
    return await appHttpService.request
      .url(`/projects/details/notes/${id}`)
      .put({ notes })
      .json<models.GetProjectNotesResponse>();
  }

  async getAllReviewers(): Promise<models.FortnoxUser[]> {
    const reviewers = await appHttpService.request
      .url('/fortnox-users')
      .get()
      .json<models.FortnoxUser[]>();
    return reviewers;
  }

  async addReviewers(
    id: string,
    reviewers: models.FortnoxUserReviewRanking[],
  ): Promise<void> {
    await appHttpService.request
      .url(`/projects/${id}/reviewers`)
      .post(reviewers)
      .json<void>();
  }

  async updateReviewers(
    id: string,
    reviewers: models.FortnoxUserReviewRanking[],
  ): Promise<void> {
    await appHttpService.request
      .url(`/projects/${id}/reviewers`)
      .put(reviewers)
      .json<void>();
  }

  async getDefaultReviewers(
    id: string,
  ): Promise<models.GetDefaultReviewersResponse> {
    const reviewers = await appHttpService.request
      .url(`/projects/${id}/reviewers`)
      .get()
      .json<models.GetDefaultReviewersResponse>();
    return reviewers;
  }

  async getProjectUsers(projectId: string) {
    const projects = await appHttpService.request
      .url(`/projects/${projectId}/users`)
      .get()
      .json<models.GetProjectUsersResponse>();
    return projects;
  }

  async getProjectReviewers(
    projectId: string,
  ): Promise<models.GetProjectReviewersResponse> {
    return await appHttpService.request
      .url(`/projects/${projectId}/eligible-reviewers`)
      .get()
      .json<models.GetProjectReviewersResponse>();
  }

  async getProjectNotes(projectId: string) {
    const notes = await appHttpService.request
      .url(`/projects/${projectId}/details/notes`)
      .get()
      .json<models.GetProjectNotesResponse>();
    return notes;
  }
  async getProjectOrganisations(projectId: string) {
    const organisations = await appHttpService.request
      .url(`/projects/${projectId}/organisations`)
      .get()
      .json<models.GetProjectOrganisationsResponse>();
    return organisations;
  }

  async getProjectOrganisationStakeholders(
    projectId: string,
    organisationId: string,
  ) {
    const stakeholders = await appHttpService.request
      .url(
        `/projects/${projectId}/organisations/${organisationId}/stakeholders`,
      )
      .get()
      .json<models.GetProjectOrganisationResponse>();
    return stakeholders;
  }

  async setProjectStakeholders(
    projectId: string,
    organisationId: string,
    body: models.PutProjectOrganisationStakeholdersBody,
  ) {
    const organisations = await appHttpService.request
      .url(
        `/projects/${projectId}/organisations/${organisationId}/stakeholders`,
      )
      .post(body)
      .res();
    return organisations;
  }
}

export const projectService = new ProjectService();
